import moment from 'moment-timezone';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import _ from 'lodash';

import { PageHeader } from '../../components/PageHeader';
import Strings from '../../Strings';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../constants';
import { useGlobalPageSize } from '../../utils/tableUtils';
import { TextInput } from '../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../components/AdvancedFilters/AdvancedFilters';
import { idRenderer, getAlertsValue, priorityRenderer, typeRenderer } from './helpers';
import { AlertSummaryWidget } from './AlertWidget';
import { actions, ALERTS_LIST_RESULT, ALERTS_PRIORITIES_SUMMARY_RESULT, DELETE_ALERT_RESULT } from './redux/actions';
import { ActionWidget, ActionButton } from '../../components/ActionWidget';
import { notificationActions } from '../../components/Notification/redux/actions';
import { DateFilters } from '../../utils/DateFilters';
import { dateFilter, getFiltersDefinition } from '../../components/AdvancedFilters/FiltersDefinition';
import AdvancedFiltersBar from '../../components/AdvancedFilters/AdvancedFiltersBar';
import { getRelevantFilters } from '../../components/AdvancedFilters/helpers';
import Table3, { columnHelper } from '../../containers/Table/Table3';
import './alerts.scss';
import { toolTipForTable } from '../../utils';

function Alerts(props) {
  const { getAlerts, deleteAlert, refreshTimestamp, showNotification, onNavigate, filters, getAlertsPrioritiesSummary, orgIds, orgs } = props;
  const [currentShowingCount, setCurrentShowingCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [highPriotyAlertsCount, setHighPriotyAlertsCount] = useState(0);
  const [mediumPriotyAlertsCount, setMediumPriotyAlertsCount] = useState(0);
  const [lowPriotyAlertsCount, setLowPriotyAlertsCount] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [pagination, setPagination] = useState({});
  const [selectedAlerts, setSelectedAlerts] = useState([]);

  const globalPageSize = useGlobalPageSize();
  const tableRef = React.useRef(null);
  const dateFiltersForPage = { ...DateFilters };
  delete dateFiltersForPage.Custom;

  const pageRequest = useRef({
    offset: 0,
    limit: globalPageSize,
    orgId: (orgIds && orgIds.length > 0) ? orgIds : undefined,
  });

  const setDefaults = () => {
    setHighPriotyAlertsCount(0);
    setMediumPriotyAlertsCount(0);
    setLowPriotyAlertsCount(0);
    setAlerts([]);
    setPagination({});
  };

  const parseResponse = response => {
    const alertsList = response?.alerts || [];
    setCurrentShowingCount(alertsList.length);
    setAlerts(alertsList);
    setPagination(response?.pagination || {});
    setIsLoading(false);
  };

  const getData = () => {
    setIsLoading(true);
    setDefaults();

    getAlerts(pageRequest.current).then(resp => {
      if (resp.type === ALERTS_LIST_RESULT) {
        parseResponse(resp.response);
      } else {
        setIsLoading(false);
      }
    }).catch(() => {
      setIsLoading(false);
    });

    getAlertsPrioritiesSummary().then(resp => {
      if (resp.type === ALERTS_PRIORITIES_SUMMARY_RESULT) {
        setLowPriotyAlertsCount(resp.response.find(r => r.priority === 0)?.count || 0);
        setMediumPriotyAlertsCount(resp.response.find(r => r.priority === 1)?.count || 0);
        setHighPriotyAlertsCount(resp.response.find(r => r.priority === 2)?.count || 0);
      }
    });
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    pageRequest.current.orgId = (orgIds && orgIds.length > 0) ? orgIds : undefined;
    pageRequest.current.offset = 0;
    getData();
  }, [refreshTimestamp, orgIds]);

  const onFiltersChange = React.useMemo(() => _.debounce(filterState => {
    const alertFilters = getRelevantFilters(filterState.filter(f => f.group === 'alertsTypes'));
    pageRequest.current.alert_type = alertFilters?.length > 0 ? alertFilters.map(f => f.value) : undefined;
    pageRequest.current.offset = 0;

    const dateFilters = getRelevantFilters(filterState.filter(f => f.group === 'dateFilter'));
    if (dateFilters?.length > 0) {
      pageRequest.current.startDate = DateFilters[dateFilters[0].value]?.dates.startDate();
      pageRequest.current.endDate = DateFilters[dateFilters[0].value]?.dates.endDate();
    } else {
      delete pageRequest.current.startDate;
      delete pageRequest.current.endDate;
    }

    getData();
  }, 1000), []);

  React.useEffect(() => {
    const alertFilters = getRelevantFilters(filters.filter(f => f.group === 'alertsTypes'));
    const alertQueryString = alertFilters?.length > 0 ? alertFilters.map(f => f.value) : undefined;

    let startDate;
    let endDate;
    const dateFilters = getRelevantFilters(filters.filter(f => f.group === 'dateFilter'));
    if (dateFilters?.length > 0) {
      startDate = DateFilters[dateFilters[0].value]?.dates.startDate();
      endDate = DateFilters[dateFilters[0].value]?.dates.endDate();
    }

    if (pageRequest.current.alert_type !== alertQueryString || 
      (pageRequest.current.startDate !== startDate) || 
      (pageRequest.current.endDate !== endDate)
    ) {
      onFiltersChange(filters);
    }
  }, [filters]);

  const pageHeaderLeftPart = isLoading ? Strings.showingWait : Strings.formatString(Strings.showingXPendingAlerts, currentShowingCount);

  const searchQueryDebounced = _.debounce(query => {
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    getData();
  }, 1000);

  const searchQueryChange = ({ target }) => {
    return searchQueryDebounced(target.value);
  };

  const pageHeaderRightPart = (
    <>
      <TextInput className="search" placeholder={Strings.search} onChange={searchQueryChange} />
      <AdvancedFilters>
        <Filter definition={dateFilter(dateFiltersForPage).dateFilter} />
        <Filter definition={getFiltersDefinition().alertsTypes} />
      </AdvancedFilters>
    </>
  );

  const pageHeader = <PageHeader left={pageHeaderLeftPart} right={pageHeaderRightPart} />;

  const summary = (
    <div className="alerts-priority-widget-container">
      <AlertSummaryWidget isLoading={isLoading} count={highPriotyAlertsCount} priority={2} />
      <AlertSummaryWidget isLoading={isLoading} count={mediumPriotyAlertsCount} priority={1} />
      <AlertSummaryWidget isLoading={isLoading} count={lowPriotyAlertsCount} priority={0} />
    </div>
  );

  const onOffsetChange = value => {
    pageRequest.current.offset = value;
    getData();
  };

  const onPageSizeChange = value => {
    pageRequest.current.limit = value;
    pageRequest.current.offset = 0;
    getData();
  };

  const onDeleteAlert = () => {
    const alerts_ids = selectedAlerts.map(a => a.id);
    alerts_ids.forEach(alert_id => {
      deleteAlert(alert_id).then(resp => {
        if (resp.type !== DELETE_ALERT_RESULT) {
          showNotification('Failed to delete alert definition');
        }
        getData();
      });
    });
  };

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    pageRequest.current.offset = 0;
    getData();
  };

  const onRowClick = alert => {
    if (alert?.patient_id) {
      const maskedId = encodeURIComponent(alert.patient_id);
      onNavigate(`/cap-patients/${maskedId}`);
    }
  };

  const columns = React.useMemo(() => {
    const ret = [
      columnHelper.accessor('id', {
        header: Strings.id,
        cell: info => idRenderer(info.getValue()),
        accessorFn : row => row,
        size: 150,
        enableHiding: false,
      }),
      columnHelper.accessor('name', {
        accessorFn: row => row.patient_name,
        header: Strings.name,
        cell: info => info.renderValue(),
        size: 250,
      }),
    ];
    if (orgs?.length > 1) {
      ret.push(columnHelper.accessor('patient_org_name', {
        header: Strings.organization,
        cell: info => info.getValue(),
        size: 200,
      }));
    }
    ret.push(columnHelper.accessor('priority', {
      header: Strings.priority,
      cell: info => priorityRenderer(info.getValue()),
      size: 120,
      }));
    ret.push(columnHelper.accessor('date', {
      header: Strings.date,
      accessorFn: row => row.created_at,
      cell: info => moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE),
      size: 220,
      }));
    ret.push(columnHelper.accessor('last_triggered_at', {
      header: Strings.lastEventDate,
      cell: info => info.getValue() ? moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE) : '-',
      size: 220,
      }));
    ret.push(columnHelper.accessor('type', {
      accessorFn: row => row.general_type,
      header: Strings.type,
      cell: info => typeRenderer(info.getValue()),
      size: 200,
    }));
    ret.push(
      columnHelper.accessor('conditions', {
        header: Strings.value,
        cell: info => {
          const row = info.row.original;
          const { value, tooltip } = getAlertsValue(row.conditions, row.extended_items) || {};
          return tooltip ? toolTipForTable(value, tooltip) : value;
        },
        accessorFn: row => getAlertsValue(row.conditions, row.extended_items)?.value,
        size: 300,
        enableSorting: false,
      }));
    return ret;
  }, [orgs]);

  const table = (
    <Table3
      isLoading={isLoading}
      name="alerts"
      data={alerts}
      pagination={pagination}
      columns={columns}
      onOffsetChange={onOffsetChange}
      onPageSizeChange={onPageSizeChange}
      onSortingChange={onSortingChange}
      onRowClick={onRowClick}
      onRowSelectionChange={setSelectedAlerts}
      enableRowSelection
      enableColumnFiltering
      ref={tableRef}
      noScroll
      pinFirstColumn
    />
  );

  const actionWidget = (
    <ActionWidget show={selectedAlerts?.length > 0}>
      <ActionButton img="action-delete" tooltiptext={Strings.delete} text={Strings.delete} action={onDeleteAlert} />
    </ActionWidget>
  );

  return (
    <div className="alerts-page">
      {summary}
      {pageHeader}
      <AdvancedFiltersBar customFilters={dateFilter(dateFiltersForPage)} />
      {table}
      {actionWidget}
    </div>
  );
}

Alerts.propTypes = {
  getAlerts: PropTypes.func.isRequired,
  deleteAlert: PropTypes.func.isRequired,
  refreshTimestamp: PropTypes.any.isRequired,
  showNotification: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  getAlertsPrioritiesSummary: PropTypes.func.isRequired,
  orgIds: PropTypes.array,
  orgs: PropTypes.array,
};

const mapStateToProps = state => {
  return {
    filters: state.entities.advancedFilters.filters.items,
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
  };
};

const mapDispatchToProps = dispatch => ({
  getAlerts: pageRequest => dispatch(actions.getAlerts(pageRequest)),
  deleteAlert: id => dispatch(actions.deleteAlert(id)),
  showNotification: (message, timeout) => dispatch(notificationActions.show(message, timeout)),
  getAlertsPrioritiesSummary: () => dispatch(actions.getAlertsPrioritiesSummary()),
  onNavigate: path => dispatch(push(path)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Alerts);
